import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import loadingLogo from "@/assets/winners-cup-loading-logo.png";

interface LoadingScreenProps {
  minDuration?: number;
}

const LoadingScreen = ({ minDuration = 1500 }: LoadingScreenProps) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, minDuration);

    return () => clearTimeout(timer);
  }, [minDuration]);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="fixed inset-0 z-[200] bg-secondary flex flex-col items-center justify-center"
        >
          {/* Background gradient */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--primary)/0.15)_0%,transparent_60%)]" />

          {/* Animated logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="relative z-10"
          >
            <motion.img
              src={loadingLogo}
              alt="Winner's Cup"
              width={384}
              height={169}
              className="w-64 sm:w-80 md:w-96 h-auto"
              animate={{
                filter: [
                  "drop-shadow(0 0 0px hsl(4, 85%, 70% / 0))",
                  "drop-shadow(0 0 30px hsl(4, 85%, 70% / 0.5))",
                  "drop-shadow(0 0 0px hsl(4, 85%, 70% / 0))",
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Loading indicator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-12 flex flex-col items-center gap-4"
          >
            {/* Spinner */}
            <div className="relative">
              <motion.div
                className="w-12 h-12 rounded-full border-4 border-primary/20"
              />
              <motion.div
                className="absolute inset-0 w-12 h-12 rounded-full border-4 border-transparent border-t-primary"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
            </div>

            {/* Loading text */}
            <motion.span
              className="text-foreground/70 text-sm font-medium tracking-wider uppercase"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              Loading
            </motion.span>
          </motion.div>

          {/* Decorative floating particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 bg-primary/40 rounded-full"
                initial={{
                  x: `${20 + Math.random() * 60}%`,
                  y: "110%",
                  opacity: 0,
                }}
                animate={{
                  y: "-10%",
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: i * 0.4,
                  ease: "linear",
                }}
              />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
